import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import SearchInput from "./components/SearchInput";
import Pagination from "./components/Pagination";
import Toast from "./components/Toast";
import DataTable from "./components/DataTable";
import ViewContentModal from "./components/ViewContentModal";

const apiUrl = import.meta.env.VITE_API_URL;

const ThirdPartyApiErrors = () => {
  const defaultSort = { key: "createdAt", direction: "desc" };
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const [viewContentModalOpen, setViewContentModalOpen] = useState(false);
  const [selectedThirdPartyApiError, setSelectedThirdPartyApiError] = useState(null);

  const searchTriggered = useRef(false);
  const searchClicked = useRef(false);
  const debounced = useRef(null);


  const columns = [
    { key: "sno", label: "Sno", sortable: false },
    { key: "model", label: "Model Name", sortable: false },
    { key: "type", label: "Type", sortable: false },
    { key: "jsonString", label: "Error", sortable: false },
    { key: "lastSeen", label: "Last Seen", sortable: false },
    { key: "count", label: "Attempt", sortable: false },
  ];

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        page: currentPage,
        limit: itemsPerPage,
      });
      const res = await fetch(`${apiUrl}/api/admin-user/third-party-api-errors?${params.toString()}`);
      const json = await res.json();
      if (res.ok) {
        setData(json.data);
        setTotalPages(json.totalPages);
      } else {
        throw new Error(json.error || "Failed to fetch data.");
      }
      setError(null);
    } catch (err) {
      setError("Failed to fetch data.");
      setToast({ show: true, message: "Failed to fetch data.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   if (!data.length) return;
  //   const sorted = [...data].sort((a, b) => {
  //     const { key, direction } = sortConfig;
  //     const aVal = a[key]?.toString().toLowerCase() ?? "";
  //     const bVal = b[key]?.toString().toLowerCase() ?? "";
  //     if (aVal < bVal) return direction === "ascending" ? -1 : 1;
  //     if (aVal > bVal) return direction === "ascending" ? 1 : -1;
  //     return 0;
  //   });
  //   setData(sorted);
  // }, [sortConfig]);

  useEffect(() => {
    if (!searchTriggered.current) fetchData();
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (searchTriggered.current) {
      searchTriggered.current = false;
      fetchData();
    }
  }, [searchTriggered.current]);

  useEffect(() => {
    if (searchClicked.current) {
      debounced.current = setTimeout(() => {
        setCurrentPage(1);
        setSortConfig(defaultSort);
        setItemsPerPage(10);
        searchTriggered.current = true;
      }, 1000);
    }
    return () => clearTimeout(debounced.current);
  }, [searchQuery]);

  const requestSort = useCallback(
    (key) => {
      let direction = "ascending";
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleOpenViewContentModal = (thirdPartyApiError) => {
    setSelectedThirdPartyApiError(thirdPartyApiError);
    setViewContentModalOpen(true);
  };

  const onAction = (item, action) => {
    if (action === "viewContent") {
      handleOpenViewContentModal(item);
    }
  };


  return (
    <div className="overflow-x-hidden">
      <div className="flex bg-background">
        <Sidebar
          variant="with-categories"
          onToggle={(collapsed) => setIsCollapsed(collapsed)}
        />
        <div
          className={`w-full transition-all duration-300 ${isCollapsed ? "lg:pl-16" : "lg:pl-64"
            }`}
        >
          <Header isCollapsed={isCollapsed} variant="default" />
          <main className="overflow-y-auto p-4 md:p-6 bg-background">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Third Party Api Errors</h1>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b w-full border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <SearchInput
                  onClick={() => (searchClicked.current = true)}
                  onBlur={() => (searchClicked.current = false)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClear={() => {
                    setSearchQuery("");
                    searchClicked.current = true;
                  }}
                  placeholder="Search third party api errors..."
                />
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Show:</span>
                  <select
                    className="form-select rounded-md border-gray-300 shadow-sm text-sm"
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    aria-label="Items per page"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                error={error}
                sortConfig={sortConfig}
                onSort={requestSort}
                onRetry={fetchData}
                emptyMessage="No third party api errors found."
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onAction={onAction}
              />

              {!isLoading && !error && data?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalItems={data.length}
                  itemsPerPage={itemsPerPage}
                  showingFrom={(currentPage - 1) * itemsPerPage + 1}
                  showingTo={Math.min(currentPage * itemsPerPage, data.length)}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}



      {viewContentModalOpen && (
        <ViewContentModal
          thirdPartyApiError={selectedThirdPartyApiError}
          onClose={() => setViewContentModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ThirdPartyApiErrors;